
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ServiceContactSchema, ServiceContactFormValues } from "./ServiceContactSchema";
import { CustomFormField } from "../for-pros/FormField";
import { Textarea } from "@/components/ui/textarea";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { submitServiceContact } from "@/utils/serviceContactUtils";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface QuoteRequestFormProps {
  serviceName: string;
}

export const QuoteRequestForm = ({ serviceName }: QuoteRequestFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  const form = useForm<ServiceContactFormValues>({
    resolver: zodResolver(ServiceContactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data: ServiceContactFormValues) => {
    // Check if honeypot field was filled (bot detection)
    if (honeypot) {
      console.log("Honeypot triggered, likely a bot submission");
      // Simulate successful submission but don't actually submit
      setTimeout(() => {
        setIsSubmitting(false);
        toast({
          title: "Quote Request Received",
          description: "We'll be in touch shortly!",
        });
        form.reset();
      }, 1000);
      return;
    }

    if (isSubmitting) return; // Prevent duplicate submissions
    
    try {
      setIsSubmitting(true);
      console.log('Starting quote request form submission with data:', data);
      
      await submitServiceContact({
        ...data,
        serviceName: `Quote Request: ${serviceName}`,
        pageUrl: window.location.href
      });
      
      toast({
        title: "Quote Request Received",
        description: "Thank you! A local professional will contact you soon.",
      });

      setSubmissionSuccess(true);
      form.reset();
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error.message || "There was a problem submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formattedServiceName = serviceName.replace(/^Best /, '').replace(/ in OKC$/, '');

  return (
    <div id="quote-request" className="py-12 bg-gradient-to-r from-primary/5 to-primary/10">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {submissionSuccess ? (
            <Card className="border-primary/20 bg-white/80 backdrop-blur">
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <h2 className="text-2xl font-bold text-primary mb-4">
                    Thank You For Your Quote Request!
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    We've received your information and will connect you with top {formattedServiceName} professionals in Oklahoma City. 
                    Expect to hear from someone shortly.
                  </p>
                  <Button 
                    onClick={() => setSubmissionSuccess(false)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Submit Another Request
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-primary/20 bg-white/80 backdrop-blur">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold text-primary">
                  Get a Free Quote from Top {formattedServiceName} in OKC
                </CardTitle>
                <p className="text-muted-foreground">
                  Fill out the form below, and we'll connect you with the best professionals in your area.
                </p>
              </CardHeader>
              <CardContent className="p-6">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    {/* Honeypot field - invisible to users, catches bots */}
                    <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 overflow-hidden">
                      <label>
                        Skip this field
                        <input type="text" onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} />
                      </label>
                    </div>
                    
                    <CustomFormField
                      control={form.control}
                      name="name"
                      label="Full Name"
                    />
                    
                    <CustomFormField
                      control={form.control}
                      name="email"
                      label="Email"
                      type="email"
                    />
                    
                    <CustomFormField
                      control={form.control}
                      name="phone"
                      label="Phone Number"
                      type="tel"
                    />
                    
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message (optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              placeholder="Tell us more about what you need"
                              className="resize-none"
                              maxLength={500}
                            />
                          </FormControl>
                          <FormMessage />
                          <div className="text-xs text-muted-foreground text-right">
                            {field.value?.length || 0}/500
                          </div>
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      className="w-full bg-primary hover:bg-primary/90 text-white"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Processing..." : "Request a Quote"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
